import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydInput from '../../commonDesign/SydInput'
import SydButton from '../../commonDesign/Button'
import { usePolicy } from '../../../hooks/usePolicy'
import { useUpdateStatusReportItemMetadataMutation } from '../../../api/groups'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import Icon from '../../atoms/Icon'
import SydDatePicker from '../../commonDesign/SydDatePicker'
import SydSwitch from '../../commonDesign/SydSwitch'
import { useStatusReportContext } from './statusReportContext'

const useStyles = makeStyles(() => ({
  readOnly: {
    color: 'var(--color-neutral-800)'
  }
}))

function MetadataEditor ({ item, metadataKey, metadataType, currentValue }) {
  const classes = useStyles()
  const { report, readOnly } = useStatusReportContext()
  const { formatter } = useFormattingContext()
  const userHasEditPolicy = usePolicy('status_report_edit')
  const canEdit = useMemo(() => !readOnly && report?.status === 'active' && userHasEditPolicy, [readOnly, report?.status, userHasEditPolicy])

  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(currentValue ?? '')

  const { mutateAsync: updateMetadata, isLoading } = useUpdateStatusReportItemMetadataMutation()

  const handleSave = useCallback(async () => {
    try {
      await updateMetadata({
        statusReportId: item.statusReportId,
        statusReportItemId: item.statusReportItemId,
        metadata: {
          [metadataKey]: value || null
        }
      })
      setIsEditing(false)
    } catch (err) {
      console.error('Error updating metadata:', err)
    }
  }, [updateMetadata, item, metadataKey, value])

  const handleCancel = useCallback(() => {
    setValue(currentValue ?? '')
    setIsEditing(false)
  }, [currentValue])

  // Format display value
  const displayValue = useMemo(() => {
    if (!currentValue) return '—'

    switch (metadataType) {
      case 'date':
        return formatter(currentValue, '@MM/DD/YY')
      case 'boolean':
        return currentValue === 'true' ? 'Yes' : 'No'
      default:
        return currentValue
    }
  }, [currentValue, metadataType, formatter])

  if (!canEdit) {
    return <div className={classes.readOnly}>{displayValue}</div>
  }

  if (!isEditing) {
    return (
      <div className='flex gap-sm items-center'>
        <SydButton
          onClick={() => setIsEditing(true)}
          variant='ghost'
          size='xs'
          style={{ minWidth: '0', padding: '6px' }}
        >
          <Icon name='edit' />
        </SydButton>
        {displayValue}
      </div>
    )
  }

  return (
    <div className='flex gap-sm'>
      <SydButton
        onClick={handleCancel}
        variant='ghost'
        size='xs'
        style={{ minWidth: '32px' }}
        disabled={isLoading}
      >
        <Icon name='close' />
      </SydButton>
      {metadataType === 'date' && (
        <SydDatePicker
          value={value ? formatter(value, '@YYYY-MM-DD') : null}
          onChange={(newValue) => setValue(formatter(newValue, '@YYYY-MM-DD'))}
          disabled={isLoading}
          size='xs'
          style={{ minWidth: 0 }}
        />
      )}

      {metadataType === 'string' && (
        <SydInput
          type='text'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={isLoading}
          size='xs'
          minWidth='0'
        />
      )}

      {metadataType === 'number' && (
        <SydInput
          type='number'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={isLoading}
          size='xs'
          minWidth='0'
        />
      )}

      {metadataType === 'boolean' && (
        <SydSwitch
          defaultValue={value === 'true'}
          onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
          disabled={isLoading}
          size='sm'
        />
      )}

      <SydButton
        onClick={handleSave}
        variant='primary'
        size='sm'
        processing={isLoading}
      >
        Apply
      </SydButton>
    </div>
  )
}

MetadataEditor.propTypes = {
  item: PropTypes.shape({
    statusReportId: PropTypes.number,
    statusReportItemId: PropTypes.number
  }).isRequired,
  metadataKey: PropTypes.string.isRequired,
  metadataType: PropTypes.oneOf(['date', 'string', 'number', 'boolean']).isRequired,
  currentValue: PropTypes.string
}

export default MetadataEditor
